import { useEffect, useMemo, useRef } from 'react';

import { useAtomValue } from 'jotai/react';
import { useInView } from 'react-intersection-observer';
import { useSearchParams } from 'react-router';

import { isValidDate } from '@core';
import { useFetchAllUserActivities, useGetActivityCount } from '@queries';
import { activityFilterAtom, boothIdAtom, useEnsureAtomValue, userAtom } from '@store';

import { useLastSelectedActivity } from './useLastSelectedActivity';

export function useActivityList() {
    const containerRef = useRef<HTMLDivElement>(null);
    const boothId = useAtomValue(boothIdAtom);
    const restoredActivityIdRef = useRef<number | null>(null);
    const [searchParams] = useSearchParams();
    const studentId = searchParams.get('studentId');
    const user = useEnsureAtomValue(userAtom);
    const userId = user?.id;
    const activitiesUserId = studentId ?? userId;
    const storedFilter = useAtomValue(activityFilterAtom);
    const filter = useMemo(
        () => ({
            ...storedFilter,
            boothId,
            startDate: isValidDate(storedFilter.startDate) ? storedFilter.startDate : null,
            endDate: isValidDate(storedFilter.endDate) ? storedFilter.endDate : null,
        }),
        [storedFilter],
    );

    const { data: totalActivities } = useGetActivityCount(activitiesUserId);
    const { lastSelectedActivityId, persistLastSelectedActivityId } = useLastSelectedActivity({
        userId: activitiesUserId,
    });
    const {
        data: activitiesPages,
        isLoading: isLoadingActivities,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        refetch,
    } = useFetchAllUserActivities(filter, activitiesUserId);

    const activities = useMemo(() => activitiesPages?.pages.flat() || [], [activitiesPages]);

    useEffect(() => {
        if (!lastSelectedActivityId) {
            restoredActivityIdRef.current = null;
            return;
        }

        if (restoredActivityIdRef.current === lastSelectedActivityId) {
            return;
        }

        const activityElement = containerRef.current?.querySelector<HTMLElement>(
            `[data-activity-id="${lastSelectedActivityId}"]`,
        );

        if (!activityElement) {
            return;
        }

        activityElement.scrollIntoView({ block: 'center' });
        restoredActivityIdRef.current = lastSelectedActivityId;
    }, [activities, lastSelectedActivityId]);

    const { ref: bottomRef } = useInView({
        initialInView: true,
        onChange: (inView) => {
            if (inView && !isLoadingActivities && hasNextPage) {
                fetchNextPage();
            }
        },
    });

    return {
        studentId,

        containerRef,
        bottomRef,

        lastSelectedActivityId,
        persistLastSelectedActivityId,

        filter,

        activities,
        totalActivities,
        isLoadingActivities,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        refetch,
    };
}
